// @ts-nocheck
/* eslint-disable */
import React from 'react';
import { QueryCache, QueryClient, QueryClientProvider } from 'react-query';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import type { HistoryServiceV3 } from '@feature-hub/history-service';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import { InViewContextProvider } from '@oneaudi/feature-app-utils';
import I18nContextComponent from '@oneaudi/i18n-context';
import type { I18NServiceV1 } from '@oneaudi/i18n-service';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { EnvConfigServiceV1 } from '@oneaudi/audi-env-config-service/dist/v1/EnvConfigServiceV1';
import type { RenderModeServiceV1 } from '@oneaudi/render-mode-service';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import type { VisitorLocatorServiceV1 } from '@volkswagen-onehub/audi-feature-service-visitor-locator';
import {
  audiVisitorLocatorServiceId,
  defineAudiVisitorLocatorService,
} from '@volkswagen-onehub/audi-feature-service-visitor-locator';
import type { AuthServiceProviderV4 } from '@oneaudi/audi-auth-service';
import { AuthContextComponent, EnvConfigContextComponent } from '@oneaudi/myaudi-kit-utils';
import type { UserVehicleFeatureServiceV1 } from '@oneaudi/user-vehicle-feature-service';
import { defineUserVehicleFeatureService } from '@oneaudi/user-vehicle-feature-service';
import type { PubSubFeatureServiceV1 } from '@volkswagen-onehub/audi-feature-service-pubsub';
import { pubSubFeatureService } from '@volkswagen-onehub/audi-feature-service-pubsub';
import type { DealerContextServiceV1 } from '@oneaudi/dealer-context-service';
import AppMap from './FeatureApp';
import type { AuthorConfig } from './models/map/author-config';
import loggerContext from './contexts/logger-context';
import type { TrackingService } from './models/tracking-service';
import trackingContext from './contexts/tracking-context';
import historyContext from './contexts/history-context';
import visitorLocatorServiceContext from './contexts/visitor-locator-service-context';
import { getDesignLibrary } from '../alpha-helper';

interface Dependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly [audiVisitorLocatorServiceId]: VisitorLocatorServiceV1;
  readonly 'dbad:audi-i18n-service': I18NServiceV1;
  readonly 'locale-service': LocaleServiceV1;
  readonly 'vw:authService': AuthServiceProviderV4;
  readonly 'user-vehicle-feature-service': UserVehicleFeatureServiceV1;
  readonly 'pubsub-service': PubSubFeatureServiceV1;
  readonly 'audi-render-mode-service'?: RenderModeServiceV1;
  readonly 'audi-content-service': ContentServiceV1;
  readonly 'dealer-context-service': DealerContextServiceV1;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, Dependencies> = {
  dependencies: {
    featureServices: {
      [audiVisitorLocatorServiceId]: '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
      'locale-service': '^1.0.0',
      'vw:authService': '^4.0.0',
      'audi:envConfigService': '^1.0.0',
      'pubsub-service': '^1.0.0',
      'user-vehicle-feature-service': '^1.0.0',
    },
    externals: {
      react: '^18',
      'styled-components': '*',
      '@feature-hub/react': '^3',
      ...getDesignLibrary({ unifiedWebCommonVersion: '*', unifiedWebAlphaCommonVersion: '*' }),
    },
  },
  ownFeatureServiceDefinitions: [
    pubSubFeatureService,
    defineAudiVisitorLocatorService(),
    defineUserVehicleFeatureService(),
  ],
  optionalDependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
      'audi-tracking-service': '^2.0.0',
      's2:logger': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      's2:history': '^3.0.0',
      'audi-render-mode-service': '^1.0.0',
      'dealer-context-service': '^1.0.0',
    },
  },

  create: ({
    featureServices,
    baseUrl,
    featureAppId,
    config,
  }: FeatureAppEnvironment<Dependencies, AuthorConfig>) => {
    const logger: Logger = featureServices['s2:logger'] || console;
    const visitorLocatorService = featureServices[audiVisitorLocatorServiceId];
    const trackingService: TrackingService = featureServices[
      'audi-tracking-service'
    ] as TrackingService;
    if (trackingService) {
      trackingService.featureAppName = 'dealer-search-map-app';
    }
    const contentService = featureServices['audi-content-service'];
    const historyService: HistoryServiceV3 = featureServices['s2:history'] as HistoryServiceV3;

    const localeServiceData = featureServices['locale-service'];
    const authServiceProvider = featureServices['vw:authService'];

    const i18nData = featureServices['dbad:audi-i18n-service'];
    const userVehicleFeatureService = featureServices['user-vehicle-feature-service'];

    logger?.info('Feature App Search created.');

    const authService = authServiceProvider.register('myaudi');
    const envConfigService = featureServices['audi:envConfigService'] as EnvConfigServiceV1;
    const dealerContextService = featureServices['dealer-context-service'];
    logger.debug('Initialized Dealer Search Map with featureAppId:', featureAppId);
    logger.debug('Initializing feature app with locale:', localeServiceData);

    const queryCacheService = {
      queryCache: new QueryCache(),
    };

    const queryClient = new QueryClient({
      queryCache: queryCacheService.queryCache,
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
    });

    return {
      render: () => {
        return (
          <UniversalEditorProvider
            contentService={featureServices['audi-content-service']}
            renderModeService={featureServices['audi-render-mode-service']}
          >
            <InViewContextProvider logger={logger}>
              <EnvConfigContextComponent
                envConfigService={envConfigService}
                envConfigKeys={['dealer-search']}
              >
                <AuthContextComponent
                  authService={
                    authService as unknown as Parameters<
                      typeof AuthContextComponent
                    >[0]['authService']
                  }
                >
                  <historyContext.Provider historyService={historyService}>
                    <loggerContext.Provider logger={logger}>
                      <trackingContext.Provider trackingService={trackingService}>
                        <I18nContextComponent
                          i18nData={i18nData}
                          language={localeServiceData.language}
                          scopes={['fa.audi-dealer-search', 'oADD']}
                        >
                          <visitorLocatorServiceContext.Provider
                            visitorLocatorService={visitorLocatorService}
                          >
                            <QueryClientProvider client={queryClient}>
                              <AppMap
                                baseUrl={baseUrl || ''}
                                locale={localeServiceData}
                                featureAppId={featureAppId}
                                userVehicleFeatureService={userVehicleFeatureService}
                                contentService={contentService}
                                config={config}
                                dealerContextService={dealerContextService}
                              />
                            </QueryClientProvider>
                          </visitorLocatorServiceContext.Provider>
                        </I18nContextComponent>
                      </trackingContext.Provider>
                    </loggerContext.Provider>
                  </historyContext.Provider>
                </AuthContextComponent>
              </EnvConfigContextComponent>
            </InViewContextProvider>
          </UniversalEditorProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
